import React from 'react';
import { Box, Skeleton, Stack, Card, alpha } from '@mui/material';
import { colors } from '../../theme';

const StockDetailSkeleton = () => {
  return (
    <Box sx={{ minHeight: '100vh' }}>
      {/* Header Skeleton - matches StockHeader exactly */}
      <Box sx={{ 
        position: 'sticky',
        top: 0,
        zIndex: 10,
        backdropFilter: 'blur(8px)',
        bgcolor: 'rgba(255, 255, 255, 0.95)',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ px: 2, py: 2 }}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            {/* Back button */}
            <Skeleton 
              variant="rounded" 
              width={40} 
              height={40}
              animation="wave"
              sx={{ 
                borderRadius: 1,
                bgcolor: colors.gray[200],
                mt: 0.5,
                flexShrink: 0
              }}
            />
            
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                {/* Ticker */}
                <Skeleton 
                  variant="text" 
                  width={100} 
                  height={44}
                  animation="wave"
                  sx={{ bgcolor: 'rgba(100, 116, 139, 0.12)' }} 
                />
                {/* Sector chip */}
                <Skeleton 
                  variant="rounded" 
                  width={100} 
                  height={26}
                  animation="wave"
                  sx={{ borderRadius: 3, bgcolor: alpha(colors.valueConfirmation.main, 0.15) }} 
                />
              </Stack>
              {/* Company name */}
              <Skeleton 
                variant="text" 
                width={350} 
                height={22}
                animation="wave"
                sx={{ mt: 0.5, mb: 1, bgcolor: 'rgba(100, 116, 139, 0.08)' }} 
              />
            </Box>

            {/* Price section - right aligned */}
            <Box sx={{ textAlign: 'right', alignSelf: 'center' }}>
              <Skeleton 
                variant="text" 
                width={120} 
                height={40}
                animation="wave"
                sx={{ bgcolor: 'rgba(16, 185, 129, 0.15)', mb: 0.5 }} 
              />
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                <Skeleton 
                  variant="text" 
                  width={60} 
                  height={20}
                  animation="wave"
                  sx={{ bgcolor: 'rgba(16, 185, 129, 0.12)' }} 
                />
                <Skeleton 
                  variant="text" 
                  width={80} 
                  height={20}
                  animation="wave"
                  sx={{ bgcolor: 'rgba(16, 185, 129, 0.12)' }} 
                />
              </Stack>
            </Box>

            {/* Refresh button skeleton */}
            <Skeleton 
              variant="rounded" 
              width={40} 
              height={40}
              animation="wave"
              sx={{ 
                borderRadius: 1,
                bgcolor: colors.gray[200],
                mt: 0.5,
                flexShrink: 0
              }}
            />
          </Stack>
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>
        {/* Chart and Financial Metrics - 2 column layout */}
        <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
          {/* Left - Chart Skeleton */}
          <Card sx={{ 
            flex: 1,
            borderRadius: '16px',
            border: `1px solid ${colors.gray[200]}`,
            boxShadow: `0 4px 12px ${alpha(colors.gray[900], 0.08)}`,
            overflow: 'hidden',
            background: '#fff'
          }}>
            <Box sx={{ p: 3 }}>
              {/* Chart header with period selector and buttons */}
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2.5 }}>
                <Skeleton 
                  variant="text" 
                  width={120} 
                  height={28}
                  animation="wave"
                  sx={{ bgcolor: 'rgba(100, 116, 139, 0.1)' }} 
                />
                <Box sx={{ flex: 1 }} />
                {/* Chart type buttons */}
                <Stack direction="row" spacing={1}>
                  {['OHLC', 'Line', 'Vol', 'MA'].map((btn, i) => (
                    <Skeleton 
                      key={i}
                      variant="rounded" 
                      width={btn === 'OHLC' ? 70 : btn === 'Line' ? 60 : btn === 'Vol' ? 50 : 45} 
                      height={36}
                      animation="wave"
                      sx={{ 
                        borderRadius: 1.5, 
                        bgcolor: i === 0 ? colors.purple.main : 'rgba(100, 116, 139, 0.08)' 
                      }} 
                    />
                  ))}
                </Stack>
              </Stack>
              
              {/* Chart area with H/L/VOL labels */}
              <Box sx={{ position: 'relative' }}>
                <Stack direction="row" spacing={3} sx={{ mb: 1.5 }}>
                  {['H (20D)', 'L (20D)', 'VOL'].map((label, i) => (
                    <Box key={i}>
                      <Skeleton 
                        variant="text" 
                        width={60} 
                        height={16}
                        animation="wave"
                        sx={{ mb: 0.5, bgcolor: 'rgba(100, 116, 139, 0.08)' }} 
                      />
                      <Skeleton 
                        variant="text" 
                        width={80} 
                        height={24}
                        animation="wave"
                        sx={{ bgcolor: i === 0 ? 'rgba(16, 185, 129, 0.12)' : i === 1 ? 'rgba(239, 68, 68, 0.12)' : 'rgba(100, 116, 139, 0.12)' }} 
                      />
                    </Box>
                  ))}
                </Stack>
                
                {/* Chart skeleton with visual elements */}
                <Box sx={{ 
                  height: 400,
                  borderRadius: 2, 
                  bgcolor: 'rgba(248, 250, 252, 1)',
                  border: '1px solid rgba(226, 232, 240, 0.5)',
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: 0.5,
                  p: 2
                }}>
                  {/* Simulated candlestick bars */}
                  {[...Array(30)].map((_, i) => (
                    <Box 
                      key={i}
                      sx={{ 
                        flex: 1,
                        height: `${Math.random() * 60 + 20}%`,
                        bgcolor: i % 3 === 0 ? 'rgba(16, 185, 129, 0.08)' : 'rgba(100, 116, 139, 0.05)',
                        borderRadius: 0.5,
                        animation: 'wave 1.6s linear 0.5s infinite',
                        animationDelay: `${i * 0.05}s`,
                        '@keyframes wave': {
                          '0%': { opacity: 0.3 },
                          '50%': { opacity: 0.6 },
                          '100%': { opacity: 0.3 }
                        }
                      }}
                    />
                  ))}
                  
                  {/* Trend line overlay */}
                  <Box sx={{
                    position: 'absolute',
                    top: '30%',
                    left: '10%',
                    right: '10%',
                    height: 2,
                    background: 'linear-gradient(90deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%)',
                    borderRadius: 1,
                    transform: 'rotate(-5deg)'
                  }} />
                </Box>
              </Box>
            </Box>
          </Card>

          {/* Right - Financial Metrics Skeleton */}
          <Card sx={{ 
            width: 320,
            flexShrink: 0,
            borderRadius: '16px',
            border: `1px solid ${colors.gray[200]}`,
            boxShadow: `0 4px 12px ${alpha(colors.gray[900], 0.08)}`,
            overflow: 'hidden',
            background: '#fff'
          }}>
            <Box sx={{ 
              p: 2.5,
              borderBottom: `1px solid ${colors.gray[200]}`,
              bgcolor: alpha(colors.purple.main, 0.03)
            }}>
              <Skeleton 
                variant="text" 
                width={140} 
                height={24}
                animation="wave"
                sx={{ bgcolor: 'rgba(139, 92, 246, 0.15)' }} 
              />
            </Box>
            <Box sx={{ p: 2.5 }}>
              <Stack spacing={2.5}>
                {/* VỐN HÓA */}
                <Box>
                  <Skeleton 
                    variant="text" 
                    width="60%" 
                    height={14}
                    animation="wave"
                    sx={{ mb: 0.5, bgcolor: 'rgba(100, 116, 139, 0.08)' }} 
                  />
                  <Skeleton 
                    variant="text" 
                    width="50%" 
                    height={32}
                    animation="wave"
                    sx={{ bgcolor: 'rgba(102, 126, 234, 0.12)' }} 
                  />
                </Box>
                
                {/* P/E and P/B */}
                <Stack direction="row" spacing={3}>
                  <Box sx={{ flex: 1 }}>
                    <Skeleton 
                      variant="text" 
                      width="40%" 
                      height={14}
                      animation="wave"
                      sx={{ mb: 0.5, bgcolor: 'rgba(100, 116, 139, 0.08)' }} 
                    />
                    <Skeleton 
                      variant="text" 
                      width="60%" 
                      height={28}
                      animation="wave"
                      sx={{ bgcolor: 'rgba(139, 92, 246, 0.12)' }} 
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Skeleton 
                      variant="text" 
                      width="40%" 
                      height={14}
                      animation="wave"
                      sx={{ mb: 0.5, bgcolor: 'rgba(100, 116, 139, 0.08)' }} 
                    />
                    <Skeleton 
                      variant="text" 
                      width="60%" 
                      height={28}
                      animation="wave"
                      sx={{ bgcolor: 'rgba(139, 92, 246, 0.12)' }} 
                    />
                  </Box>
                </Stack>
                
                {/* ROE and ROA */}
                <Stack direction="row" spacing={3}>
                  <Box sx={{ flex: 1 }}>
                    <Skeleton 
                      variant="text" 
                      width="40%" 
                      height={14}
                      animation="wave"
                      sx={{ mb: 0.5, bgcolor: 'rgba(100, 116, 139, 0.08)' }} 
                    />
                    <Skeleton 
                      variant="text" 
                      width="70%" 
                      height={28}
                      animation="wave"
                      sx={{ bgcolor: 'rgba(16, 185, 129, 0.12)' }} 
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Skeleton 
                      variant="text" 
                      width="40%" 
                      height={14}
                      animation="wave"
                      sx={{ mb: 0.5, bgcolor: 'rgba(100, 116, 139, 0.08)' }} 
                    />
                    <Skeleton 
                      variant="text" 
                      width="70%" 
                      height={28}
                      animation="wave"
                      sx={{ bgcolor: 'rgba(16, 185, 129, 0.12)' }} 
                    />
                  </Box>
                </Stack>
                
                {/* D/E and EPS */}
                <Stack direction="row" spacing={3}>
                  <Box sx={{ flex: 1 }}>
                    <Skeleton 
                      variant="text" 
                      width="40%" 
                      height={14}
                      animation="wave"
                      sx={{ mb: 0.5, bgcolor: 'rgba(100, 116, 139, 0.08)' }} 
                    />
                    <Skeleton 
                      variant="text" 
                      width="50%" 
                      height={28}
                      animation="wave"
                      sx={{ bgcolor: 'rgba(139, 92, 246, 0.12)' }} 
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Skeleton 
                      variant="text" 
                      width="40%" 
                      height={14}
                      animation="wave"
                      sx={{ mb: 0.5, bgcolor: 'rgba(100, 116, 139, 0.08)' }} 
                    />
                    <Skeleton 
                      variant="text" 
                      width="60%" 
                      height={28}
                      animation="wave"
                      sx={{ bgcolor: 'rgba(139, 92, 246, 0.12)' }} 
                    />
                  </Box>
                </Stack>
                
                {/* KL TB */}
                <Box>
                  <Skeleton 
                    variant="text" 
                    width="70%" 
                    height={14}
                    animation="wave"
                    sx={{ mb: 0.5, bgcolor: 'rgba(100, 116, 139, 0.08)' }} 
                  />
                  <Skeleton 
                    variant="text" 
                    width="40%" 
                    height={28}
                    animation="wave"
                    sx={{ bgcolor: 'rgba(139, 92, 246, 0.12)' }} 
                  />
                </Box>
              </Stack>
            </Box>
          </Card>
        </Box>

        {/* Signals Grid Skeleton - matches SignalsGrid exactly */}
        <Card sx={{ 
          borderRadius: '16px',
          border: `1px solid ${colors.gray[200]}`,
          boxShadow: `0 4px 12px ${alpha(colors.gray[900], 0.08)}`,
          overflow: 'hidden',
          background: '#fff',
          mb: 3
        }}>
          {/* Header with gradient background */}
          <Box sx={{ 
            p: 3,
            borderBottom: `1px solid ${colors.gray[200]}`,
            background: `linear-gradient(135deg, ${alpha(colors.valueConfirmation.main, 0.05)} 0%, ${alpha(colors.purple.main, 0.05)} 100%)`,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <Skeleton 
              variant="rounded" 
              width={48} 
              height={48}
              animation="wave"
              sx={{ 
                borderRadius: '12px', 
                bgcolor: '#fff',
                boxShadow: `0 2px 8px ${alpha(colors.valueConfirmation.main, 0.15)}`
              }} 
            />
            <Box sx={{ flex: 1 }}>
              <Skeleton 
                variant="text" 
                width={180} 
                height={28}
                animation="wave"
                sx={{ mb: 0.25, bgcolor: 'rgba(100, 116, 139, 0.15)' }} 
              />
              <Skeleton 
                variant="text" 
                width={250} 
                height={18}
                animation="wave"
                sx={{ bgcolor: 'rgba(100, 116, 139, 0.1)' }} 
              />
            </Box>
            <Skeleton 
              variant="rounded" 
              width={120} 
              height={32}
              animation="wave"
              sx={{ 
                borderRadius: 3, 
                bgcolor: colors.valueConfirmation.main,
                opacity: 0.3
              }} 
            />
          </Box>
          
          {/* Body - 2 Column Layout */}
          <Box sx={{ p: 2.5, display: 'flex', gap: 2.5 }}>
            {/* Left - Signals Table (65%) */}
            <Box sx={{ flex: '0 0 65%' }}>
              <Box sx={{ 
                border: `1px solid ${colors.gray[200]}`,
                borderRadius: '8px',
                overflow: 'hidden'
              }}>
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Box 
                    key={i}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 1.5,
                      borderBottom: i < 6 ? `1px solid ${colors.gray[200]}` : 'none'
                    }}
                  >
                    {/* Label */}
                    <Skeleton 
                      variant="text" 
                      width={100} 
                      height={16}
                      animation="wave"
                      sx={{ bgcolor: 'rgba(100, 116, 139, 0.1)' }} 
                    />
                    
                    {/* Progress Bar */}
                    <Box sx={{ flex: 1 }}>
                      <Skeleton 
                        variant="rounded" 
                        width="100%" 
                        height={4}
                        animation="wave"
                        sx={{ 
                          borderRadius: '2px',
                          bgcolor: i % 3 === 0 ? 'rgba(16, 185, 129, 0.2)' : i % 3 === 1 ? 'rgba(239, 68, 68, 0.2)' : 'rgba(100, 116, 139, 0.2)' 
                        }} 
                      />
                    </Box>
                    
                    {/* Value */}
                    <Skeleton 
                      variant="text" 
                      width={60} 
                      height={20}
                      animation="wave"
                      sx={{ bgcolor: i % 3 === 0 ? 'rgba(16, 185, 129, 0.15)' : i % 3 === 1 ? 'rgba(239, 68, 68, 0.15)' : 'rgba(100, 116, 139, 0.15)' }} 
                    />
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Right - Reasons (35%) */}
            <Box sx={{ flex: '0 0 35%' }}>
              <Box sx={{ 
                border: `1px solid ${colors.gray[200]}`,
                borderRadius: '8px',
                p: 2
              }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                  <Skeleton 
                    variant="rounded" 
                    width={32} 
                    height={32}
                    animation="wave"
                    sx={{ borderRadius: 1.5, bgcolor: 'rgba(139, 92, 246, 0.1)' }} 
                  />
                  <Skeleton 
                    variant="text" 
                    width={120} 
                    height={20}
                    animation="wave"
                    sx={{ bgcolor: 'rgba(139, 92, 246, 0.12)' }} 
                  />
                  <Skeleton 
                    variant="rounded" 
                    width={24} 
                    height={24}
                    animation="wave"
                    sx={{ borderRadius: '50%', bgcolor: colors.purple.main, opacity: 0.3 }} 
                  />
                </Stack>
                <Stack spacing={1.5}>
                  {[1, 2, 3].map((i) => (
                    <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                      <Skeleton 
                        variant="circular" 
                        width={18} 
                        height={18}
                        animation="wave"
                        sx={{ mt: 0.25, bgcolor: 'rgba(16, 185, 129, 0.2)', flexShrink: 0 }} 
                      />
                      <Skeleton 
                        variant="text" 
                        width={i === 3 ? "80%" : "100%"} 
                        height={16}
                        animation="wave"
                        sx={{ flex: 1, bgcolor: 'rgba(100, 116, 139, 0.08)' }} 
                      />
                    </Stack>
                  ))}
                </Stack>
              </Box>
            </Box>
          </Box>
        </Card>

        {/* AI Analysis Skeleton - matches AIAnalysisCard exactly */}
        <Card sx={{ 
          borderRadius: '16px',
          border: `1px solid ${colors.gray[200]}`,
          boxShadow: `0 4px 12px ${alpha(colors.gray[900], 0.08)}`,
          overflow: 'hidden',
          background: '#fff'
        }}>
          {/* Header with AI badge */}
          <Box sx={{ 
            p: 3,
            borderBottom: `1px solid ${colors.gray[200]}`,
            background: `linear-gradient(135deg, ${alpha(colors.purple.main, 0.05)} 0%, ${alpha(colors.valueConfirmation.main, 0.05)} 100%)`
          }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Skeleton 
                variant="rounded" 
                width={48} 
                height={48}
                animation="wave"
                sx={{ 
                  borderRadius: '12px', 
                  bgcolor: colors.purple.main,
                  opacity: 0.2
                }} 
              />
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
                  <Skeleton 
                    variant="text" 
                    width={140} 
                    height={26}
                    animation="wave"
                    sx={{ bgcolor: 'rgba(139, 92, 246, 0.15)' }} 
                  />
                  <Skeleton 
                    variant="rounded" 
                    width={32} 
                    height={20}
                    animation="wave"
                    sx={{ borderRadius: 1, bgcolor: colors.purple.main, opacity: 0.3 }} 
                  />
                </Stack>
                <Skeleton 
                  variant="text" 
                  width={260} 
                  height={18}
                  animation="wave"
                  sx={{ bgcolor: 'rgba(100, 116, 139, 0.1)' }} 
                />
              </Box>
            </Stack>
          </Box>
          
          {/* Content */}
          <Box sx={{ p: 3 }}>
            {/* Style badge */}
            <Box sx={{ mb: 2.5 }}>
              <Skeleton 
                variant="rounded" 
                width={100} 
                height={24}
                animation="wave"
                sx={{ borderRadius: 1.5, bgcolor: 'rgba(139, 92, 246, 0.12)' }} 
              />
            </Box>
            
            {/* Analysis text */}
            <Stack spacing={1.5} sx={{ mb: 3 }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton 
                  key={i}
                  variant="text" 
                  width={i === 5 ? "75%" : i === 4 ? "90%" : "100%"}
                  height={18}
                  animation="wave"
                  sx={{ bgcolor: 'rgba(100, 116, 139, 0.06)' }} 
                />
              ))}
            </Stack>
            
            {/* Score and recommendation */}
            <Stack direction="row" spacing={3} sx={{ mb: 2.5, pt: 2.5, borderTop: `1px solid ${colors.gray[200]}` }}>
              <Box sx={{ flex: 1 }}>
                <Skeleton 
                  variant="text" 
                  width={80} 
                  height={16}
                  animation="wave"
                  sx={{ mb: 0.5, bgcolor: 'rgba(100, 116, 139, 0.08)' }} 
                />
                <Skeleton 
                  variant="text" 
                  width={60} 
                  height={32}
                  animation="wave"
                  sx={{ bgcolor: 'rgba(16, 185, 129, 0.15)' }} 
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Skeleton 
                  variant="text" 
                  width={100} 
                  height={16}
                  animation="wave"
                  sx={{ mb: 0.5, bgcolor: 'rgba(100, 116, 139, 0.08)' }} 
                />
                <Skeleton 
                  variant="rounded" 
                  width={80} 
                  height={28}
                  animation="wave"
                  sx={{ borderRadius: 1.5, bgcolor: 'rgba(16, 185, 129, 0.2)' }} 
                />
              </Box>
            </Stack>
          </Box>
        </Card>
      </Box>
    </Box>
  );
};

export default StockDetailSkeleton;

